import { useState, useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Badge,
  Text,
  Spinner,
  FormControl,
  Input,
  Button,
} from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import PageLoader from "./components/PageLoader";
import api from "./api";

interface ReferralData {
  address: string;
  referred: string[];
  referralCount: number;
  createdAt?: string;
}

interface RewardDisbursementData {
  date: string;
  totalRewards: number;
  totalStaked: number;
  activeStakes: number;
  uniqueUsers: number;
  transactions: number;
}

const DATE_PRESETS = [7, 14, 30, 90] as const;
type DatePreset = (typeof DATE_PRESETS)[number] | null;

function formatDateForInput(date: Date) {
  return date.toISOString().slice(0, 10);
}

function getDateRangeFromPreset(days: number) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  return {
    startDate: formatDateForInput(start),
    endDate: formatDateForInput(end),
  };
}

function StakingLeaderboard() {
  const [stackingData, setStackingData] = useState<any[]>([]);
  const [leaderboardData, setLeaderboardData] = useState<any[]>([]);
  const [referralsData, setReferralsData] = useState<ReferralData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [leaderboardError, setLeaderboardError] = useState("");
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralsError, setReferralsError] = useState("");
  const [rewardDisbursementData, setRewardDisbursementData] = useState<RewardDisbursementData[]>([]);
  const [rewardDisbursementLoading, setRewardDisbursementLoading] = useState(false);
  const [rewardDisbursementError, setRewardDisbursementError] = useState("");
  const [dateRange, setDateRange] = useState(() => getDateRangeFromPreset(90));
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>(90);

  const fetchStackingData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/api/admin/staking/history");
      setStackingData(res?.data?.data?.stakings || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Error loading stacking data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboardData = async () => {
    setLeaderboardError("");
    try {
      const res = await api.get(
        "/api/admin/staking/leaderboard?page=1&limit=100"
      );
      console.log("Leaderboard response:", res.data);
      // API returns { status: true, data: { leaderboard: [...], total: ... } }
      const leaderboard = res?.data?.data?.leaderboard;
      console.log(
        "Extracted leaderboard:",
        leaderboard,
        "Is array:",
        Array.isArray(leaderboard)
      );
      // Ensure it's always an array
      const leaderboardArray = Array.isArray(leaderboard) ? leaderboard : [];
      console.log(
        "Setting leaderboardData to:",
        leaderboardArray.length,
        "items"
      );
      setLeaderboardData(leaderboardArray);
    } catch (err: any) {
      console.error("Leaderboard error:", err);
      setLeaderboardError(
        err?.response?.data?.message ||
          err?.message ||
          "Error loading leaderboard data"
      );
      // Set empty array on error to prevent map error
      setLeaderboardData([]);
    }
  };

  const fetchReferralsData = async () => {
    setReferralsLoading(true);
    setReferralsError("");
    try {
      const res = await api.get("/api/admin/staking/referrals");
      const referrals = res?.data?.data || [];
      setReferralsData(referrals);
    } catch (err: any) {
      setReferralsError(
        err?.response?.data?.message || "Error loading referrals data"
      );
    } finally {
      setReferralsLoading(false);
    }
  };

  const fetchRewardDisbursementData = async () => {
    setRewardDisbursementLoading(true);
    setRewardDisbursementError("");
    try {
      const res = await api.get("/api/admin/staking/reward-disbursement");
      console.log("Reward disbursement response:", res.data);
      setRewardDisbursementData(res?.data?.data || []);
    } catch (err: any) {
      console.error("Reward disbursement error:", err);
      const errorMessage = err?.response?.data?.message 
        || err?.response?.data?.error 
        || err?.message 
        || "Error loading reward disbursement data";
      setRewardDisbursementError(errorMessage);
    } finally {
      setRewardDisbursementLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStackingData(), fetchLeaderboardData(), fetchReferralsData(), fetchRewardDisbursementData()]).finally(() => {
      setLoading(false);
    });
  }, []);

  const isDateInRange = (dateStr: string) => {
    if (!dateRange.startDate && !dateRange.endDate) return true;
    const d = new Date(dateStr);
    const start = dateRange.startDate ? new Date(dateRange.startDate) : new Date(0);
    const end = dateRange.endDate ? new Date(dateRange.endDate) : new Date();
    end.setHours(23, 59, 59, 999);
    return d >= start && d <= end;
  };

  const filteredStackingData = stackingData.filter(
    (item: any) => isDateInRange(item?.updatedAt || item?.createdAt || "")
  );
  const filteredReferralsData = referralsData.filter(
    (r: ReferralData) => isDateInRange(r?.createdAt || "")
  );
  const filteredRewardDisbursementData = rewardDisbursementData.filter(
    (day: RewardDisbursementData) => isDateInRange(day?.date || "")
  );

  return (
    <Container maxW="container.xl" py={8}>
      <Toaster position="top-right" />
      {loading && <PageLoader message="Loading staking data..." />}

      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="xl" textAlign="center">
          Staking & Leaderboard
        </Heading>

        <HStack spacing={4} flexWrap="wrap">
          <FormControl maxW="200px">
            <Input
              type="date"
              placeholder="Start Date"
              value={dateRange.startDate}
              onChange={(e) => {
                setDateRange({ ...dateRange, startDate: e.target.value });
                setSelectedPreset(null);
              }}
            />
          </FormControl>
          <FormControl maxW="200px">
            <Input
              type="date"
              placeholder="End Date"
              value={dateRange.endDate}
              onChange={(e) => {
                setDateRange({ ...dateRange, endDate: e.target.value });
                setSelectedPreset(null);
              }}
            />
          </FormControl>
          <HStack spacing={2}>
            {DATE_PRESETS.map((days) => (
              <Button
                key={days}
                size="sm"
                variant={selectedPreset === days ? "solid" : "outline"}
                colorScheme={selectedPreset === days ? "blue" : "gray"}
                onClick={() => {
                  setDateRange(getDateRangeFromPreset(days));
                  setSelectedPreset(days);
                }}
              >
                {days}d
              </Button>
            ))}
          </HStack>
          {(dateRange.startDate || dateRange.endDate) && (
            <Button
              size="sm"
              onClick={() => {
                setDateRange({ startDate: "", endDate: "" });
                setSelectedPreset(null);
              }}
            >
              Clear Filters
            </Button>
          )}
        </HStack>

        <Tabs isFitted variant="enclosed" bg="gray.800" borderRadius="12px" border="1px solid" borderColor="gray.700" defaultIndex={0}>
          <TabList mb="1em" bg="gray.750" borderTopRadius="12px">
            <Tab
              color="gray.400"
              fontWeight="500"
              px={6}
              py={3}
              _hover={{ color: "white", bg: "gray.700" }}
              _selected={{
                color: "white",
                bg: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                borderColor: "brand.400",
                fontWeight: "700",
                boxShadow: "0 4px 15px rgba(99, 102, 241, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)",
                transform: "translateY(-1px)",
              }}
            >
              Staking
            </Tab>
            <Tab
              color="gray.400"
              fontWeight="500"
              px={6}
              py={3}
              _hover={{ color: "white", bg: "gray.700" }}
              _selected={{
                color: "white",
                bg: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                borderColor: "brand.400",
                fontWeight: "700",
                boxShadow: "0 4px 15px rgba(99, 102, 241, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)",
                transform: "translateY(-1px)",
              }}
            >
              Leaderboard
            </Tab>
            <Tab
              color="gray.400"
              fontWeight="500"
              px={6}
              py={3}
              _hover={{ color: "white", bg: "gray.700" }}
              _selected={{
                color: "white",
                bg: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                borderColor: "brand.400",
                fontWeight: "700",
                boxShadow: "0 4px 15px rgba(99, 102, 241, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)",
                transform: "translateY(-1px)",
              }}
            >
              Referrals
            </Tab>
            <Tab
              color="gray.400"
              fontWeight="500"
              px={6}
              py={3}
              _hover={{ color: "white", bg: "gray.700" }}
              _selected={{
                color: "white",
                bg: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                borderColor: "brand.400",
                fontWeight: "700",
                boxShadow: "0 4px 15px rgba(99, 102, 241, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)",
                transform: "translateY(-1px)",
              }}
            >
              Reward Disbursement
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <Box overflowX="auto">
                {error ? (
                  <Alert status="error">
                    <AlertIcon />
                    {error}
                  </Alert>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Index</Th>
                        <Th>Address</Th>
                        <Th>Amount</Th>
                        <Th>Position ID</Th>
                        <Th>Early Exit</Th>
                        <Th>Withdrawn</Th>
                        <Th>Time</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredStackingData?.map((item: any, idx) => (
                        <Tr key={item?.address + idx}>
                          <Td>{idx + 1}</Td>
                          <Td>
                            {item?.address?.slice(0, 6)}...
                            {item?.address?.slice(-3)}
                          </Td>
                          <Td>{(+item?.amount)?.toFixed(2)}</Td>
                          <Td>{item?.positionId ?? "-"}</Td>
                          <Td>{item?.isEarlyExit ? "Yes" : "No"}</Td>
                          <Td>{item?.isWithdrawn ? "Yes" : "No"}</Td>
                          <Td>
                            {item?.createdAt
                              ? new Date(item.updatedAt).toLocaleString()
                              : ""}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Box>
            </TabPanel>
            <TabPanel>
              <Box overflowX="auto">
                {leaderboardError ? (
                  <Alert status="error">
                    <AlertIcon />
                    {leaderboardError}
                  </Alert>
                ) : leaderboardData.length === 0 ? (
                  <Box textAlign="center" py={10} color="gray.400">
                    No leaderboard data available
                  </Box>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Rank</Th>
                        <Th>Wallet</Th>
                        <Th>Total XP</Th>
                        <Th>Level</Th>
                        <Th>Daily XP</Th>
                        <Th>Total Staked</Th>
                        <Th>Referrals</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(Array.isArray(leaderboardData)
                        ? leaderboardData
                        : []
                      ).map((row: any, idx) => (
                        <Tr key={row?.address + idx || idx}>
                          <Td>{row?.rank || idx + 1}</Td>
                          <Td>
                            {row?.address?.slice(0, 6)}...
                            {row?.address?.slice(-3)}
                          </Td>
                          <Td>{row?.totalXP?.toFixed(3)}</Td>
                          <Td>{row?.level ?? 0}</Td>
                          <Td>{row?.dailyXP?.toFixed(5)}</Td>
                          <Td>{row?.totalStaked?.toFixed(5)}</Td>
                          <Td>{row?.referralCount ?? 0}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Box>
            </TabPanel>
            <TabPanel>
              <Box overflowX="auto">
                {referralsLoading ? (
                  <Box textAlign="center" py={10}>
                    <Spinner color="brand.500" size="lg" />
                  </Box>
                ) : referralsError ? (
                  <Alert status="error" bg="red.900" borderColor="red.600">
                    <AlertIcon />
                    {referralsError}
                  </Alert>
                ) : filteredReferralsData.length === 0 ? (
                  <Box textAlign="center" py={10} color="gray.400">
                    No referrals data available
                  </Box>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>No.</Th>
                        <Th>Address</Th>
                        <Th>Joining Date</Th>
                        <Th>Referrals</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredReferralsData?.map((referral: ReferralData, idx) => (
                        <Tr key={referral?.address + idx}>
                          <Td>{idx + 1}</Td>
                          <Td>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Text fontFamily="mono" fontSize="sm">
                                {referral?.address?.slice(0, 8)}...
                                {referral?.address?.slice(-6)}
                              </Text>
                              <CopyIcon
                                cursor="pointer"
                                color="gray.400"
                                _hover={{ color: "white" }}
                                onClick={() => {
                                  navigator.clipboard.writeText(referral?.address);
                                  toast.success("Address copied!");
                                }}
                              />
                            </Box>
                          </Td>
                          <Td>
                            <Text fontSize="sm">
                              {referral?.createdAt
                                ? new Date(referral.createdAt).toLocaleString()
                                : "N/A"}
                            </Text>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={2}>
                              {referral?.referred?.map((ref: string, index: number) => (
                                <Box key={index} display="flex" alignItems="center" gap={2}>
                                  <Badge colorScheme="purple" px={2} py={1}>
                                    <Text fontFamily="mono" fontSize="xs">
                                      {ref}
                                    </Text>
                                  </Badge>
                                  <CopyIcon
                                    cursor="pointer"
                                    color="gray.400"
                                    _hover={{ color: "white" }}
                                    boxSize={3}
                                    onClick={() => {
                                      navigator.clipboard.writeText(ref);
                                      toast.success("Address copied!");
                                    }}
                                  />
                                </Box>
                              ))}
                              {(!referral?.referred || referral.referred.length === 0) && (
                                <Text color="gray.500" fontSize="sm">No referrals</Text>
                              )}
                            </VStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Box>
            </TabPanel>
            <TabPanel>
              <Box overflowX="auto">
                {rewardDisbursementLoading ? (
                  <Box textAlign="center" py={10}>
                    <Spinner color="brand.500" size="lg" />
                  </Box>
                ) : rewardDisbursementError ? (
                  <Alert status="error">
                    <AlertIcon />
                    {rewardDisbursementError}
                  </Alert>
                ) : filteredRewardDisbursementData.length === 0 ? (
                  <Box textAlign="center" py={10} color="gray.400">
                    No reward disbursement data available
                  </Box>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Date</Th>
                        <Th isNumeric>Total Rewards</Th>
                        <Th isNumeric>Total Staked</Th>
                        <Th isNumeric>Active Stakes</Th>
                        <Th isNumeric>Unique Users</Th>
                        <Th isNumeric>Transactions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredRewardDisbursementData?.map((day: RewardDisbursementData, idx) => (
                        <Tr key={day.date + idx}>
                          <Td>
                            <Text fontWeight="500">
                              {new Date(day.date).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text color="white" fontWeight="600">
                              {day.totalRewards?.toLocaleString(undefined, { 
                                minimumFractionDigits: 2, 
                                maximumFractionDigits: 6 
                              })} USDC
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text color="white" fontWeight="600">
                              {day.totalStaked?.toLocaleString(undefined, { 
                                minimumFractionDigits: 2, 
                                maximumFractionDigits: 2 
                              })} USDC
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text color="white">
                              {day.activeStakes}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text color="white">
                              {day.uniqueUsers}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text color="white">
                              {day.transactions}
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
}

export default StakingLeaderboard;
